/**
 * Brain — o assistente da clínica dentro da VecchIA (trilhas B4 e B5).
 *
 * Os tipos seguem os contratos das trilhas B1 (função `brain`, `brain-voz`,
 * tabelas `brain_*` e balde `brain`) e B6 (projetos, preferências, pedidos,
 * modelos, texto e link — `docs/API_CONTRACTS.md`). Moram aqui, e não em
 * `tipos.ts`, porque o módulo é inteiro novo e traz regra junto: o que se pode
 * anexar, o catálogo do Personalizar e como cada recusa do servidor vira frase.
 */

import type { FluxoDePcm } from './fluxoDePcm';

export type ModoDoBrain = 'auto' | 'rapido';

export type PapelNaConversa = 'usuario' | 'assistente';

export type SituacaoDaProposta = 'pendente' | 'confirmada' | 'cancelada' | 'falhou' | 'expirada';

export type IdiomaDoBrain = 'pt-BR' | 'en' | 'es';

/** Só feminina desde 13/09/2026: a voz da VecchIA é a Gacrux. A `masculina` antiga no banco vira feminina. */
export type VozDoBrain = 'feminina';

/**
 * Por que a voz reserva falou, quando falou (`X-Brain-Reserva`).
 *
 * Separa o que a tela precisa dizer: `cota-do-dia` volta na virada e pede limite maior no
 * painel do Google; `cota` passa em minutos; `falha` é para tentar de novo; `sem-chave` é
 * configuração. Sem isso o aviso dizia só "não respondeu", que parece defeito.
 */
export type MotivoDaVozReserva = 'cota-do-dia' | 'cota' | 'falha' | 'sem-chave';

const MOTIVOS_DA_VOZ_RESERVA: MotivoDaVozReserva[] = ['cota-do-dia', 'cota', 'falha', 'sem-chave'];

/** O cabeçalho vem do servidor, então nunca é confiado cru: o que não estiver na lista é `falha`. */
export function motivoDaVozReserva(bruto: string | null | undefined): MotivoDaVozReserva | null {
    if (!bruto) return null;
    return MOTIVOS_DA_VOZ_RESERVA.find((m) => m === bruto) ?? 'falha';
}

/** O áudio que a voz devolveu e quem o gerou (`X-Brain-Provedor` e `X-Brain-Voz`). */
export interface FalaGerada {
    /** Vazio quando a fala vem em `fluxo`. */
    audio: Blob;
    /** A fala chegando aos poucos, quando pedida com `fluxo` e a borda gerou agora (B11). */
    fluxo?: FluxoDePcm;
    provedor: string | null;
    voz: string | null;
    /** Nulo quando foi a Gacrux. */
    reserva?: MotivoDaVozReserva | null;
    /** Quando a Gacrux volta a ser tentada, em ISO; nulo quando não há pausa. */
    reservaAte?: string | null;
}

export type ProvedorDeIa = 'anthropic' | 'openai' | 'gemini' | 'grok' | 'openrouter';

export interface ConversaDoBrain {
    id: string;
    titulo: string;
    criadoEm: string;
    atualizadoEm: string;
    /** Retenção de 90 dias: depois disto a conversa some sozinha. */
    expiraEm: string;
    /** Projeto em que a conversa está, ou `null` fora de projeto. */
    projetoId: string | null;
}

/** Arquivo que a pessoa anexou. `caminho` é no balde privado `brain`. */
export interface AnexoDoBrain {
    caminho: string;
    nome: string;
    /** Tipo MIME. */
    tipo: string;
}

/** Arquivo que o Brain gerou. `url` é o caminho no balde; o endereço assinado sai no clique. */
export interface ArquivoGeradoDoBrain {
    id: string;
    nome: string;
    tipo: string;
    url: string;
}

export interface CampoDaProposta {
    rotulo: string;
    valor: string;
}

/**
 * Uma ação que o Brain PROPÕE e só executa com confirmação explícita (regra 8).
 * `resultado` e `motivo` são opcionais: o que foi feito, ou por que falhou.
 */
export interface PropostaDoBrain {
    id: string;
    ferramenta: string;
    resumo: string;
    campos: CampoDaProposta[];
    situacao: SituacaoDaProposta;
    resultado?: string;
    motivo?: string;
    /** Em `propor_reconectar_canal` confirmada: o canal que o cartão da conversa reconecta. */
    canalId?: string;
}

export interface FonteDaResposta {
    titulo: string;
    url: string;
}

export interface MensagemDoBrain {
    id: string;
    papel: PapelNaConversa;
    conteudo: string;
    criadoEm: string;
    anexos: AnexoDoBrain[];
    arquivos: ArquivoGeradoDoBrain[];
    propostas: PropostaDoBrain[];
    /** Quem respondeu. O servidor guarda nas mensagens do assistente. */
    provedor?: string;
    modelo?: string;
    /** Só na resposta recém-chegada: o servidor não guarda fontes, documentos e avisos. */
    fontes?: FonteDaResposta[];
    documentos?: { titulo: string }[];
    avisos?: string[];
}

/** O que a tela manda. A clínica o adaptador acrescenta — ela não é escolha da tela. */
export interface EnvioAoBrain {
    conversaId?: string;
    mensagem: string;
    anexos?: AnexoDoBrain[];
    modo?: ModoDoBrain;
    /** Sem provedor, vale a chave preferida da plataforma. */
    provedor?: ProvedorDeIa;
    /** `auto`, `rapido` ou o id de `brainModelos()`. */
    modelo?: string;
    idioma?: IdiomaDoBrain;
    buscarNaWeb?: boolean;
    buscarNosDocumentos?: boolean;
    /** Só vale ao criar conversa nova. */
    projetoId?: string;
    /** Mensagem da pessoa a partir da qual o servidor apaga e responde de novo. Exige `conversaId`. */
    editarAPartirDe?: string;
    /** Um por envio: repetir o mesmo não gera nem cobra de novo. */
    pedidoId?: string;
    /** Conversa por voz: o servidor devolve também a `fala`, curta, e o texto completo vai ao chat. */
    modoVoz?: boolean;
}

export interface RespostaDoBrain {
    conversaId: string;
    mensagem: MensagemDoBrain;
    consumo: { tokens: number };
    pedidoId: string | null;
    /** Pedido repetido: é a resposta guardada, não uma nova. */
    repetido: boolean;
    mensagemDaPessoa: { id: string; criadoEm: string } | null;
    modelo: { provedor: string; modelo: string } | null;
    fontes: FonteDaResposta[];
    documentos: { titulo: string }[];
    avisos: string[];
    /** Quantas mensagens a edição apagou. */
    removidas: number;
    /** Só na conversa por voz: o que a voz fala — curto, com o aviso de que o detalhe está no chat. */
    fala: string | null;
}

/** O que `GET brain/pedidos/<id>` responde: para reencontrar a resposta depois de queda ou de parar. */
export type PedidoDoBrain =
    | { situacao: 'processando' }
    | { situacao: 'falhou'; erro: string }
    | { situacao: 'desconhecido' }
    | { situacao: 'pronta'; conversaId: string; mensagens: MensagemDoBrain[]; fala?: string | null };

export interface ProjetoDoBrain {
    id: string;
    nome: string;
    criadoEm: string;
}

export interface ModeloDoBrain {
    id: string;
    nome: string;
    descricao: string;
}

export interface ProvedorDoBrain {
    provedor: ProvedorDeIa;
    nome: string;
    buscaNaWeb: boolean;
    modelos: ModeloDoBrain[];
}

/** `GET brain/modelos`: só os provedores com chave ativa. */
export interface ModelosDoBrain {
    provedores: ProvedorDoBrain[];
    padrao: { provedor: ProvedorDeIa; modelo: string } | null;
    documentos: boolean;
    voz: { transcrever: boolean; falar: boolean };
}

/** `intencao`: a frase da conversa por voz decide a ação em foco? (B9) Responde confirmar, cancelar ou outra. */
export type AcaoDeTexto = 'gramatica' | 'melhorar' | 'escrever' | 'traduzir' | 'intencao';

/** `POST brain/link`: o texto de uma página, para anexar à mensagem. */
export interface LinkAprendido {
    url: string;
    titulo: string;
    tipo: string;
    texto: string;
    truncado: boolean;
}

/** Detalhes que o servidor manda junto da recusa e que mudam a frase da tela. */
export interface DetalhesDaRecusa {
    motivo?: string;
    situacao?: string;
    provedor?: string;
    anexo?: string;
}

/** Recusa do servidor com o status, para a tela explicar cada caso do jeito certo. */
export class ErroDoBrain extends Error {
    readonly status: number;
    readonly detalhes: DetalhesDaRecusa;

    constructor(status: number, mensagem: string, detalhes: DetalhesDaRecusa = {}) {
        super(mensagem);
        this.name = 'ErroDoBrain';
        this.status = status;
        this.detalhes = detalhes;
    }
}

/* ------------------------------------------------------------ personalizar */

export type TomDoBrain = 'formal' | 'balanced' | 'casual' | 'direct';
export type DetalheDoBrain = 'concise' | 'balanced' | 'detailed';
export type ProatividadeDoBrain = 'reactive' | 'balanced' | 'proactive';
export type EstiloDoBrain = 'collaborative' | 'instructional' | 'executive' | 'analytical';
export type EmojiDoBrain = 'never' | 'sparingly' | 'often';

/** As 7 chaves que o banco exige, com os valores do catálogo do Brain original. */
export interface ComportamentoDoBrain {
    toneStyle: TomDoBrain;
    verbosity: DetalheDoBrain;
    proactivity: ProatividadeDoBrain;
    conversationStyle: EstiloDoBrain;
    emojiUsage: EmojiDoBrain;
    askClarifyingQuestions: boolean;
    useBulletPoints: boolean;
}

export interface HabilidadeDoBrain {
    id: string;
    nome: string;
    descricao: string;
    ativa: boolean;
}

export interface PreferenciasDoBrain {
    comportamento: ComportamentoDoBrain;
    instrucoes: string;
    habilidades: HabilidadeDoBrain[];
    voz: VozDoBrain;
    idioma: IdiomaDoBrain;
}

export interface OpcaoDoCatalogo<T extends string> {
    id: T;
    label: string;
    hint: string;
}

export const OPCOES_DE_TOM: OpcaoDoCatalogo<TomDoBrain>[] = [
    { id: 'formal', label: 'Formal', hint: 'Profissional, objetivo e respeitoso.' },
    { id: 'balanced', label: 'Equilibrado', hint: 'Amigável sem perder clareza.' },
    { id: 'casual', label: 'Descontraído', hint: 'Leve, próximo e conversacional.' },
    { id: 'direct', label: 'Direto', hint: 'Vai ao ponto, sem rodeios.' },
];

export const OPCOES_DE_DETALHE: OpcaoDoCatalogo<DetalheDoBrain>[] = [
    { id: 'concise', label: 'Conciso', hint: 'Respostas curtas; só o essencial.' },
    { id: 'balanced', label: 'Equilibrado', hint: 'Detalhe moderado com boa legibilidade.' },
    { id: 'detailed', label: 'Detalhado', hint: 'Explicações completas e contexto extra.' },
];

export const OPCOES_DE_PROATIVIDADE: OpcaoDoCatalogo<ProatividadeDoBrain>[] = [
    { id: 'reactive', label: 'Reativo', hint: 'Responde só o que foi pedido.' },
    { id: 'balanced', label: 'Equilibrado', hint: 'Sugere próximos passos quando fizer sentido.' },
    { id: 'proactive', label: 'Proativo', hint: 'Antecipa necessidades e propõe ações.' },
];

export const OPCOES_DE_ESTILO: OpcaoDoCatalogo<EstiloDoBrain>[] = [
    { id: 'collaborative', label: 'Colaborativo', hint: 'Trabalha com você como parceiro.' },
    { id: 'instructional', label: 'Didático', hint: 'Ensina passo a passo quando necessário.' },
    { id: 'executive', label: 'Executivo', hint: 'Foco em decisões, impacto e prioridades.' },
    { id: 'analytical', label: 'Analítico', hint: 'Dados, comparações e raciocínio estruturado.' },
];

export const OPCOES_DE_EMOJI: OpcaoDoCatalogo<EmojiDoBrain>[] = [
    { id: 'never', label: 'Nunca', hint: 'Sem emojis nas respostas.' },
    { id: 'sparingly', label: 'Com moderação', hint: 'Emojis só quando reforçam o tom.' },
    { id: 'often', label: 'Frequente', hint: 'Tom mais expressivo com emojis.' },
];

export const LIMITE_DAS_INSTRUCOES = 2000;
export const LIMITE_DE_HABILIDADES = 30;

export const COMPORTAMENTO_PADRAO: ComportamentoDoBrain = {
    toneStyle: 'balanced',
    verbosity: 'balanced',
    proactivity: 'balanced',
    conversationStyle: 'collaborative',
    emojiUsage: 'sparingly',
    askClarifyingQuestions: true,
    useBulletPoints: true,
};

export const PREFERENCIAS_PADRAO: PreferenciasDoBrain = {
    comportamento: COMPORTAMENTO_PADRAO,
    instrucoes: '',
    habilidades: [],
    voz: 'feminina',
    idioma: 'pt-BR',
};

function escolha<T extends string>(valor: unknown, opcoes: OpcaoDoCatalogo<T>[], padrao: T): T {
    return opcoes.some((o) => o.id === valor) ? valor as T : padrao;
}

function registro(valor: unknown): Record<string, unknown> {
    return typeof valor === 'object' && valor !== null ? valor as Record<string, unknown> : {};
}

/**
 * Preferência vinda de qualquer lugar (banco, cache, rascunho) com a forma que o
 * banco aceita: valor fora do catálogo volta ao padrão, e não vira `23514`.
 */
export function normalizarPreferencias(bruto: unknown): PreferenciasDoBrain {
    const p = registro(bruto);
    const c = registro(p.comportamento);
    const habilidades = Array.isArray(p.habilidades) ? p.habilidades : [];
    return {
        comportamento: {
            toneStyle: escolha(c.toneStyle, OPCOES_DE_TOM, COMPORTAMENTO_PADRAO.toneStyle),
            verbosity: escolha(c.verbosity, OPCOES_DE_DETALHE, COMPORTAMENTO_PADRAO.verbosity),
            proactivity: escolha(c.proactivity, OPCOES_DE_PROATIVIDADE, COMPORTAMENTO_PADRAO.proactivity),
            conversationStyle: escolha(c.conversationStyle, OPCOES_DE_ESTILO, COMPORTAMENTO_PADRAO.conversationStyle),
            emojiUsage: escolha(c.emojiUsage, OPCOES_DE_EMOJI, COMPORTAMENTO_PADRAO.emojiUsage),
            askClarifyingQuestions: c.askClarifyingQuestions !== false,
            useBulletPoints: c.useBulletPoints !== false,
        },
        instrucoes: String(p.instrucoes ?? '').slice(0, LIMITE_DAS_INSTRUCOES),
        habilidades: habilidades.slice(0, LIMITE_DE_HABILIDADES).map((h, i) => {
            const r = registro(h);
            return {
                id: String(r.id ?? `habilidade-${String(i + 1)}`).slice(0, 64) || `habilidade-${String(i + 1)}`,
                nome: String(r.nome ?? '').slice(0, 80) || 'Nova habilidade',
                descricao: String(r.descricao ?? '').slice(0, 500),
                ativa: r.ativa !== false,
            };
        }),
        voz: 'feminina',
        idioma: p.idioma === 'en' || p.idioma === 'es' ? p.idioma : 'pt-BR',
    };
}

/** O resumo que o Personalizar mostra: o que as escolhas pedem ao Brain, em uma frase cada. */
export function resumoDoComportamento(p: PreferenciasDoBrain): string {
    const rotulo = <T extends string>(opcoes: OpcaoDoCatalogo<T>[], id: T) => opcoes.find((o) => o.id === id)?.label ?? id;
    const c = p.comportamento;
    const partes = [
        `Tom ${rotulo(OPCOES_DE_TOM, c.toneStyle).toLowerCase()}`,
        `detalhe ${rotulo(OPCOES_DE_DETALHE, c.verbosity).toLowerCase()}`,
        `postura ${rotulo(OPCOES_DE_PROATIVIDADE, c.proactivity).toLowerCase()}`,
        `estilo ${rotulo(OPCOES_DE_ESTILO, c.conversationStyle).toLowerCase()}`,
        `emojis: ${rotulo(OPCOES_DE_EMOJI, c.emojiUsage).toLowerCase()}`,
    ];
    const extras = [
        c.askClarifyingQuestions ? 'pergunta quando falta informação essencial' : 'não faz perguntas de esclarecimento',
        c.useBulletPoints ? 'organiza em listas e tópicos' : 'responde em texto corrido',
    ];
    const ativas = p.habilidades.filter((h) => h.ativa).length;
    const fim = [
        p.instrucoes.trim() ? 'segue as suas instruções personalizadas' : '',
        ativas > 0 ? `usa ${String(ativas)} habilidade${ativas === 1 ? '' : 's'} ativa${ativas === 1 ? '' : 's'}` : '',
    ].filter(Boolean);
    return `${partes.join(', ')}; ${[...extras, ...fim].join('; ')}. As regras da VecchIA vêm antes: nenhuma preferência dispensa a confirmação de uma ação.`;
}

/* ------------------------------------------------------------------ anexos */

/** O mesmo teto do balde `brain`. Recusar antes poupa a subida inteira. */
export const TAMANHO_MAXIMO_DO_ANEXO = 20 * 1024 * 1024;

interface TipoAceito {
    grupo: string;
    /** Tipos MIME que o navegador costuma informar para a extensão. */
    mimes: string[];
}

const TIPOS_ACEITOS: Record<string, TipoAceito> = {
    pdf: { grupo: 'PDF', mimes: ['application/pdf'] },
    doc: { grupo: 'Word', mimes: ['application/msword'] },
    docx: { grupo: 'Word', mimes: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'] },
    xls: { grupo: 'planilha', mimes: ['application/vnd.ms-excel'] },
    xlsx: { grupo: 'planilha', mimes: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'] },
    /* O Windows informa CSV como planilha do Excel. */
    csv: { grupo: 'planilha', mimes: ['text/csv', 'application/vnd.ms-excel'] },
    xml: { grupo: 'XML de nota fiscal', mimes: ['application/xml', 'text/xml'] },
    /* Texto e JSON o servidor lê (B1); é por texto que o "aprender de link" anexa a página. */
    txt: { grupo: 'texto', mimes: ['text/plain'] },
    md: { grupo: 'texto', mimes: ['text/markdown', 'text/plain', 'text/x-markdown'] },
    json: { grupo: 'JSON', mimes: ['application/json'] },
    png: { grupo: 'imagem', mimes: ['image/png'] },
    jpg: { grupo: 'imagem', mimes: ['image/jpeg'] },
    jpeg: { grupo: 'imagem', mimes: ['image/jpeg'] },
    webp: { grupo: 'imagem', mimes: ['image/webp'] },
};

/** Para o `accept` do seletor de arquivo. É só conveniência: quem valida é `problemaDoAnexo`. */
export const ACEITA_NO_SELETOR = Object.keys(TIPOS_ACEITOS).map((e) => `.${e}`).join(',');

/** Os grupos do menu "+" do original, dentro do que o servidor lê. */
export const ACEITA_POR_GRUPO = {
    imagem: '.png,.jpg,.jpeg,.webp',
    notaFiscal: '.xml,.pdf,.png,.jpg,.jpeg,.webp',
    documento: '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.md,.json,.xml',
} as const;

export const TIPOS_ACEITOS_POR_EXTENSO = 'PDF, Word, planilha (XLS, XLSX, CSV), XML de nota fiscal, texto (TXT, MD, JSON) ou imagem (PNG, JPG, WEBP)';

function extensao(nome: string): string {
    const ponto = nome.lastIndexOf('.');
    return ponto < 0 ? '' : nome.slice(ponto + 1).toLowerCase();
}

/**
 * Por que o arquivo não pode ir, ou `null` se pode.
 *
 * Extensão e tipo precisam concordar: um executável renomeado para `.pdf` chega
 * com outro tipo. Tipo vazio passa, porque há navegador que não informa XML —
 * o servidor lê o arquivo de novo e recusa com 422 o que não conseguir abrir.
 */
export function problemaDoAnexo(arquivo: { name: string; type: string; size: number }): string | null {
    const aceito = TIPOS_ACEITOS[extensao(arquivo.name)];
    if (!aceito) return `${arquivo.name} não pode ser anexado. Envie ${TIPOS_ACEITOS_POR_EXTENSO}.`;
    if (arquivo.type && !aceito.mimes.includes(arquivo.type.toLowerCase())) {
        return `${arquivo.name} diz ser ${aceito.grupo}, mas o conteúdo é de outro tipo (${arquivo.type}).`;
    }
    if (arquivo.size === 0) return `${arquivo.name} está vazio.`;
    if (arquivo.size > TAMANHO_MAXIMO_DO_ANEXO) {
        const mb = (arquivo.size / 1024 / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
        return `${arquivo.name} tem ${mb} MB; o limite é 20 MB.`;
    }
    return null;
}

/* ------------------------------------------------------------------ erros */

export interface ErroExplicado {
    titulo: string;
    detalhe: string;
    /** `aviso` quando há o que esperar ou corrigir; `perigo` quando o pedido quebrou. */
    tom: 'warning' | 'danger';
}

/**
 * Cada recusa do servidor pede uma conduta diferente, então cada uma tem a sua
 * frase. "Erro 402" não diz a quem administra a clínica que o teto do mês
 * acabou e volta na virada.
 */
export function explicarErroDoBrain(e: unknown): ErroExplicado {
    const mensagem = e instanceof Error ? e.message : String(e);
    const status = e instanceof ErroDoBrain ? e.status : 0;
    const detalhes = e instanceof ErroDoBrain ? e.detalhes : {};
    switch (status) {
        case 402:
            return {
                titulo: 'A IA desta clínica alcançou o teto do mês',
                detalhe: 'A VecchIA volta a responder na virada do mês, ou antes se a plataforma aumentar o teto.',
                tom: 'warning',
            };
        case 503:
            return {
                titulo: 'Nenhuma chave de IA está configurada na plataforma',
                detalhe: 'A VecchIA só responde quando a plataforma ligar uma chave de provedor. Nada do que você escreveu foi perdido.',
                tom: 'warning',
            };
        case 403:
            return {
                titulo: 'Sem permissão para isto',
                detalhe: mensagem || 'Durante uma impersonação a VecchIA não age, e cada ação exige a permissão do módulo.',
                tom: 'warning',
            };
        case 404:
            return {
                titulo: 'Não encontrado',
                detalhe: mensagem || 'A conversa ou o projeto não existe mais, ou não é seu.',
                tom: 'warning',
            };
        case 409:
            if (detalhes.situacao === 'processando') {
                return {
                    titulo: 'Esta mensagem ainda está sendo respondida',
                    detalhe: 'Aguarde um instante: a resposta aparece aqui quando ficar pronta.',
                    tom: 'warning',
                };
            }
            if (detalhes.motivo === 'proposta_decidida') {
                return {
                    titulo: 'Não dá para editar a partir desta mensagem',
                    detalhe: 'Depois dela há uma ação já confirmada, que falhou ou em confirmação. Envie uma mensagem nova.',
                    tom: 'warning',
                };
            }
            return { titulo: 'O pedido conflita com o estado atual', detalhe: mensagem, tom: 'warning' };
        case 422:
            if (detalhes.motivo === 'modelo_sem_chave') {
                return {
                    titulo: 'Este modelo não tem chave ativa',
                    detalhe: 'Escolha outro modelo no seletor: só aparecem os provedores com chave ligada na plataforma.',
                    tom: 'warning',
                };
            }
            if (detalhes.anexo || !detalhes.motivo) {
                return {
                    titulo: 'Não foi possível ler o anexo',
                    detalhe: mensagem || 'O arquivo está corrompido ou protegido por senha.',
                    tom: 'warning',
                };
            }
            return { titulo: 'O pedido foi recusado', detalhe: mensagem, tom: 'warning' };
        default:
            return { titulo: 'Não foi possível falar com a VecchIA', detalhe: mensagem, tom: 'danger' };
    }
}

/**
 * O texto que vai para a voz: sem a marcação do markdown, que a síntese leria
 * como "asterisco asterisco".
 */
export function textoParaFala(markdown: string): string {
    return markdown
        .replace(/```[\s\S]*?```/g, ' ')
        .replace(/`([^`]*)`/g, '$1')
        .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
        .replace(/^\s{0,3}(#{1,6}|>|[-*+]|\d+[.)])\s+/gm, '')
        .replace(/[*_~|]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

/** Identificador de um envio. `crypto.randomUUID` existe em todo navegador que roda a VecchIA. */
export function novoPedidoId(): string {
    return crypto.randomUUID();
}
